import React, { useState, useContext, useRef, useEffect } from "react";
import { 
  Container, 
  TextField, 
  Button, 
  Typography, 
  Box, 
  Paper,
  InputAdornment,
  IconButton
} from "@mui/material";
import { 
  Email as EmailIcon, 
  Lock as LockIcon, 
  Visibility, 
  VisibilityOff,
  LoginOutlined,
  ArrowBack
} from "@mui/icons-material";
import api from "../../api/api";
import { useSnackbar } from "notistack";
import { useNavigate } from "react-router-dom";
import { ThemeContext } from "../context/ThemeContext";
import AuthService from "../../../services/AuthService";
import "../../../Landing.css";

function Login() {
  const [credentials, setCredentials] = useState({
    email: "",
    password: "",
  });
  const [showPassword, setShowPassword] = useState(false);

  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const { fetchTheme } = useContext(ThemeContext);

  const [pending, setPending] = useState(false);
  const submitting = useRef(false);
  const mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setCredentials((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting.current) return;
    submitting.current = true;
    setPending(true);
    try {
      const response = await api.post("/login/", credentials);
      if (!mounted.current) return;
      const { access_token, role, redirect_to, message, subscription_expired } = response.data;
      if (!access_token) throw new Error("Missing access token");
      localStorage.setItem("token", access_token);
      localStorage.setItem("role", role);
      localStorage.setItem("user_email", credentials.email);
      if (subscription_expired !== undefined) {
        localStorage.setItem("subscription_expired", subscription_expired);
      } else {
        localStorage.removeItem("subscription_expired");
      }
      try {
        await AuthService.fetchAndUpdateUserData();
      } catch {
        // The authenticated session remains usable if optional profile loading fails.
      }
      if (!mounted.current) return;
      try {
        await fetchTheme();
      } catch {
        enqueueSnackbar("Не удалось загрузить оформление", { variant: "warning" });
      }
      if (!mounted.current) return;
      if (redirect_to) {
        navigate(redirect_to);
      } else if (role === "founder" || role === "manager") {
        navigate("/dashboard");
      } else {
        enqueueSnackbar("Неизвестная роль пользователя", { variant: "error" });
        return;
      }
      enqueueSnackbar(message || "Вход выполнен успешно", { variant: "success" });
    } catch {
      if (mounted.current) enqueueSnackbar("Ошибка при входе. Проверьте данные.", { variant: "error" });
    } finally {
      submitting.current = false;
      if (mounted.current) setPending(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0878ff 0%, #244a77 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 2
      }}
    >
      <Container maxWidth="sm">
        <Paper
          elevation={24}
          sx={{
            padding: { xs: 3, sm: 5 },
            borderRadius: 4,
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(20px)',
            boxShadow: '0 25px 50px rgba(0, 0, 0, 0.2)',
            position: 'relative',
            overflow: 'hidden'
          }}
          className="fade-in"
        >
          {/* Декоративный градиент сверху */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: 4,
              background: 'linear-gradient(90deg, #0878ff, #244a77, #a5652c, #ba834e)'
            }}
          />

          {/* Кнопка "Назад" */}
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate('/')}
            sx={{
              mb: 2,
              color: '#0878ff',
              '&:hover': {
                backgroundColor: 'rgba(102, 126, 234, 0.1)'
              }
            }}
          >
            На главную
          </Button>

          {/* Заголовок */}
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Box
              sx={{
                width: 80,
                height: 80,
                background: 'linear-gradient(135deg, #0878ff 0%, #244a77 100%)',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 20px',
                animation: 'float 3s ease-in-out infinite'
              }}
            >
              <LoginOutlined sx={{ fontSize: 40, color: 'white' }} />
            </Box>
            
            <Typography 
              variant="h4" 
              gutterBottom
              sx={{
                background: 'linear-gradient(135deg, #0878ff 0%, #244a77 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                fontWeight: 'bold',
                marginBottom: 1
              }}
            >
              Добро пожаловать!
            </Typography>
            
            <Typography 
              variant="body1" 
              color="text.secondary"
              sx={{ fontSize: '1.1rem' }}
            >
              Войдите в свой аккаунт для продолжения
            </Typography>
          </Box>

          {/* Форма */}
          <form onSubmit={handleSubmit}>
            <TextField
              label="Email"
              name="email"
              type="email"
              value={credentials.email}
              onChange={handleChange}
              fullWidth
              margin="normal"
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailIcon sx={{ color: '#0878ff' }} />
                  </InputAdornment>
                ),
                sx: {
                  borderRadius: 2,
                  '& .MuiOutlinedInput-root': {
                    '&:hover fieldset': {
                      borderColor: '#0878ff'
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#0878ff'
                    }
                  }
                }
              }}
              sx={{
                mb: 2,
                '& .MuiInputLabel-root.Mui-focused': {
                  color: '#0878ff'
                }
              }}
            />
            
            <TextField
              label="Пароль"
              name="password"
              type={showPassword ? 'text' : 'password'}
              value={credentials.password}
              onChange={handleChange}
              fullWidth
              margin="normal"
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockIcon sx={{ color: '#0878ff' }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label={showPassword ? "Скрыть пароль" : "Показать пароль"}
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
                sx: {
                  borderRadius: 2,
                  '& .MuiOutlinedInput-root': {
                    '&:hover fieldset': {
                      borderColor: '#0878ff'
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#0878ff'
                    }
                  }
                }
              }}
              sx={{
                mb: 3,
                '& .MuiInputLabel-root.Mui-focused': {
                  color: '#0878ff'
                }
              }}
            />
            
            <Button
              variant="contained"
              type="submit"
              disabled={pending}
              fullWidth
              size="large"
              className="gradient-button pulse-on-hover"
              sx={{
                background: 'linear-gradient(135deg, #0878ff 0%, #244a77 100%)',
                color: 'white',
                fontWeight: 'bold',
                fontSize: '1.1rem',
                padding: '15px',
                borderRadius: 3,
                textTransform: 'none',
                boxShadow: '0 8px 25px rgba(102, 126, 234, 0.3)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
                  boxShadow: '0 12px 35px rgba(102, 126, 234, 0.4)',
                  transform: 'translateY(-2px)'
                },
                transition: 'all 0.3s ease'
              }}
            >
              Войти в систему
            </Button>
          </form>

          {/* Ссылка на регистрацию */}
          <Box sx={{ textAlign: 'center', mt: 3 }}>
            <Typography variant="body2" color="text.secondary">
              Нет аккаунта?{' '}
              <Button
                variant="text"
                onClick={() => navigate('/register-founder')}
                sx={{
                  color: '#0878ff',
                  fontWeight: 'bold',
                  textTransform: 'none',
                  '&:hover': {
                    backgroundColor: 'transparent',
                    textDecoration: 'underline'
                  }
                }}
              >
                Зарегистрироваться
              </Button>
            </Typography>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
}

export default Login;
